import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  IconWebsiteAudit,
  IconKeywords,
  IconSEOIntelligence,
  IconCompetitors,
  IconIntegrations,
} from '../components/icons';
import {
  AIAssistantConversation,
  AIAssistantMessage,
  AIAssistantSource,
  AIAssistantAction,
  aiAssistantApi,
  businessApi,
  googleApi,
  competitorsApi,
  intelligenceApi,
  Business,
  WebsiteAudit,
  GoogleStatusResponse,
} from '../lib/api';
import './AIAssistantPage.css';

interface StarterOption {
  category: string;
  icon: string;
  prompt: string;
  desc: string;
}

const STARTER_OPTIONS: StarterOption[] = [
  {
    category: 'Audit Priority',
    icon: '🔍',
    prompt: 'What should I fix first from the website audit?',
    desc: 'Identify high-severity crawl errors, broken links, and quick meta wins.',
  },
  {
    category: 'Search Traffic',
    icon: '📈',
    prompt: 'Why did my search impressions or clicks change?',
    desc: 'Inspect recent Search Console impressions, clicks, and ranking shifts.',
  },
  {
    category: 'SEO Opportunities',
    icon: '🎯',
    prompt: 'Show my highest-impact SEO opportunities.',
    desc: 'Review high-upside recommendations ranked by traffic potential and effort.',
  },
  {
    category: 'Keyword Strategy',
    icon: '🔑',
    prompt: 'Which target keywords need immediate attention?',
    desc: 'Find page-2 keywords close to top 10 and underperforming queries.',
  },
  {
    category: 'Competitor Intel',
    icon: '⚔️',
    prompt: 'Compare me with top competitors.',
    desc: 'Benchmark content volume, technical health, and keyword overlap.',
  },
  {
    category: 'Weekly Action Plan',
    icon: '📋',
    prompt: 'What should I work on this week?',
    desc: 'Get an actionable step-by-step checklist for your local SEO growth.',
  },
];

const QUICK_PROMPTS = [
  { label: '✨ Fix Audit Errors', prompt: 'What should I fix first from the website audit?' },
  { label: '📈 Traffic Changes', prompt: 'Why did my search impressions or clicks change?' },
  { label: '🎯 Top Opportunities', prompt: 'Show my highest-impact SEO opportunities.' },
  { label: '🔑 High-Value Keywords', prompt: 'Which target keywords need immediate attention?' },
  { label: '⚔️ Competitor Gaps', prompt: 'Compare me with top competitors.' },
  { label: '📋 Weekly Plan', prompt: 'What should I work on this week?' },
];

function formatDate(value: string) {
  try {
    const d = new Date(value);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return `Today, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    try {
      void navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  }

  return (
    <button type="button" className="assistant-tool-btn" onClick={handleCopy} aria-label="Copy response">
      {copied ? '✓ Copied' : '📋 Copy response'}
    </button>
  );
}

function getSourceClass(type: string) {
  switch (type.toUpperCase()) {
    case 'AUDIT':
    case 'WEBSITE_AUDIT':
      return 'type-audit';
    case 'SEARCH_CONSOLE':
    case 'GSC':
      return 'type-gsc';
    case 'OPPORTUNITY':
      return 'type-opportunity';
    case 'COMPETITOR':
      return 'type-competitor';
    default:
      return '';
  }
}

function getSourceIcon(type: string) {
  switch (type.toUpperCase()) {
    case 'AUDIT':
    case 'WEBSITE_AUDIT':
      return '🔍';
    case 'SEARCH_CONSOLE':
    case 'GSC':
      return '📊';
    case 'OPPORTUNITY':
      return '🎯';
    case 'COMPETITOR':
      return '⚔️';
    default:
      return '📎';
  }
}

function ConversationMessage({ message }: { message: AIAssistantMessage }) {
  const isUser = message.role === 'USER';

  return (
    <div className={`assistant-message-row ${isUser ? 'is-user' : 'is-ai'}`}>
      <div className={`assistant-avatar ${isUser ? 'user' : 'ai'}`}>
        {isUser ? 'You' : '✨'}
      </div>
      <div className="assistant-bubble-wrap">
        <div className="assistant-message-header">
          <span className="assistant-message-sender">{isUser ? 'You' : 'Local SEO AI Copilot'}</span>
          <span>·</span>
          <span>{formatDate(message.createdAt)}</span>
        </div>
        <div className="assistant-bubble">
          {message.content}

          {/* Evidence Drawer */}
          {!isUser && Boolean(message.sourcesJson?.length) && (
            <div className="assistant-evidence-box">
              <div className="assistant-evidence-title">
                <span>📎 Grounded Evidence Sources</span>
              </div>
              <div className="assistant-evidence-chips">
                {message.sourcesJson?.map((source, i) => (
                  <span
                    className={`assistant-evidence-chip ${getSourceClass(source.type)}`}
                    key={`${source.type}-${source.label}-${i}`}
                  >
                    <span>{getSourceIcon(source.type)}</span>
                    <span>{source.label}</span>
                    {source.freshness && (
                      <span className="assistant-evidence-freshness">({source.freshness})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Quick Actions */}
          {!isUser && Boolean(message.actionsJson?.length) && (
            <div className="assistant-actions-box">
              {message.actionsJson?.map((action, i) => (
                <Link key={`${action.route}-${i}`} to={action.route} className="assistant-action-btn">
                  <span>→</span>
                  <span>{action.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Message Toolbar */}
        {!isUser && (
          <div className="assistant-bubble-toolbar">
            <CopyButton text={message.content} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AIAssistantPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const id = Number(businessId);

  // Conversation state
  const [conversations, setConversations] = useState<AIAssistantConversation[]>([]);
  const [selected, setSelected] = useState<string>();
  const [messages, setMessages] = useState<AIAssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showContextPanel, setShowContextPanel] = useState(true);

  // Business Context Signals
  const [business, setBusiness] = useState<Business | null>(null);
  const [latestAudit, setLatestAudit] = useState<WebsiteAudit | null>(null);
  const [googleStatus, setGoogleStatus] = useState<GoogleStatusResponse | null>(null);
  const [competitorCount, setCompetitorCount] = useState<number>(0);
  const [opportunityCount, setOpportunityCount] = useState<number>(0);
  const [loadingContext, setLoadingContext] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selected),
    [conversations, selected]
  );

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter((c) => c.title.toLowerCase().includes(q));
  }, [conversations, searchQuery]);

  async function reload() {
    try {
      setLoadingConversations(true);
      const list = await aiAssistantApi.list(id);
      setConversations(list);
    } catch {
      setError('We could not load your AI Assistant conversations.');
    } finally {
      setLoadingConversations(false);
    }
  }

  // Initial load: conversations & live business telemetry signals
  useEffect(() => {
    setSelected(undefined);
    setMessages([]);
    void reload();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setLoadingContext(true);

    Promise.allSettled([
      businessApi.get(id),
      businessApi.listAudits(id, { limit: 1 }),
      googleApi.getStatus(id),
      competitorsApi.list(id, { pageSize: 1 }),
      intelligenceApi.getSummary(id),
    ])
      .then(([bizRes, auditsRes, gRes, compRes, intelRes]) => {
        if (!mounted) return;
        if (bizRes.status === 'fulfilled') setBusiness(bizRes.value);
        if (auditsRes.status === 'fulfilled' && auditsRes.value.length > 0) {
          setLatestAudit(auditsRes.value[0]);
        }
        if (gRes.status === 'fulfilled') setGoogleStatus(gRes.value);
        if (compRes.status === 'fulfilled') setCompetitorCount(compRes.value.pagination.totalItems);
        if (intelRes.status === 'fulfilled') setOpportunityCount(intelRes.value.totalOpportunities);
        setLoadingContext(false);
      })
      .catch(() => {
        if (mounted) setLoadingContext(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  // Scroll to bottom on new message or sending state
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages.length, sending]);

  async function open(conversationId: string) {
    try {
      setError('');
      const conversation = await aiAssistantApi.get(id, conversationId);
      setSelected(conversation.id);
      setMessages(conversation.messages ?? []);
    } catch {
      setError('We could not open that conversation.');
    }
  }

  async function send(event?: FormEvent, customValue?: string) {
    event?.preventDefault();
    const text = (customValue ?? input).trim();
    if (!text || sending) return;

    const pendingId = `pending-${Date.now()}`;
    setSending(true);
    setError('');
    setInput('');

    // Optimistic UI update
    setMessages((current) => [
      ...current,
      { id: pendingId, role: 'USER', content: text, createdAt: new Date().toISOString() },
    ]);

    try {
      const result = await aiAssistantApi.chat(id, text, selected);
      setSelected(result.conversationId);
      setMessages((current) => [
        ...current.filter((message) => message.id !== pendingId),
        {
          ...result.message,
          sourcesJson: result.sources,
          actionsJson: result.suggestedActions,
        },
      ]);
      await reload();
    } catch (e: any) {
      setMessages((current) => current.filter((message) => message.id !== pendingId));
      setError(e?.message || 'The AI Assistant is temporarily unavailable. Please try again.');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  function startNewConversation() {
    setSelected(undefined);
    setMessages([]);
    setError('');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }

  async function handleDeleteConversation(convId: string, e?: React.MouseEvent) {
    e?.stopPropagation();
    try {
      await aiAssistantApi.remove(id, convId);
      if (selected === convId) {
        startNewConversation();
      }
      await reload();
    } catch {
      setError('Failed to delete conversation.');
    }
  }

  return (
    <DashboardLayout
      title="AI Assistant"
      subtitle={`Local SEO Copilot · ${business?.name ?? 'Business Workspace'}`}
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: business?.name ?? 'Business', to: `/businesses/${id}/dashboard` },
        { label: 'AI Assistant' },
      ]}
    >
      <div className="assistant-page-container">
        {/* Compact Header Bar */}
        <header className="assistant-header-bar">
          <div className="assistant-header-left">
            <div className="assistant-header-eyebrow">
              <span>AI SEO Intelligence Workspace</span>
              <span className="assistant-badge-pill">✨ Stored Grounding v2.0</span>
            </div>
            <h1 className="assistant-header-title">
              AI SEO Assistant
              {business?.name && <span style={{ fontWeight: 400, color: '#64748b', fontSize: '1rem' }}>— {business.name}</span>}
            </h1>
            <p className="assistant-header-subtitle">
              Ask practical questions grounded in your technical audits, Google Search Console metrics, and competitor telemetry.
            </p>
          </div>

          <div className="assistant-header-actions">
            <button
              type="button"
              className={`assistant-btn-secondary ${showContextPanel ? 'is-active' : ''}`}
              onClick={() => setShowContextPanel((v) => !v)}
              title="Toggle Live SEO Context Signals panel"
            >
              <span>ℹ️</span>
              <span>{showContextPanel ? 'Hide Context' : 'Show Context'}</span>
            </button>
            <button
              type="button"
              className="assistant-btn-primary"
              onClick={startNewConversation}
            >
              <span>+</span>
              <span>New Conversation</span>
            </button>
          </div>
        </header>

        {/* 3-Column Studio Grid */}
        <div className={`assistant-workspace-grid ${!showContextPanel ? 'context-collapsed' : ''}`}>
          {/* Left Panel: Conversation History */}
          <aside className="assistant-history-panel" aria-label="Conversation History">
            <div className="assistant-history-header">
              <div className="assistant-history-title">
                <span>💬 Conversations</span>
                <span className="assistant-history-count">{conversations.length}</span>
              </div>
              <button
                type="button"
                className="assistant-btn-secondary"
                style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                onClick={startNewConversation}
              >
                + New
              </button>
            </div>

            <div className="assistant-history-search">
              <input
                type="text"
                className="assistant-search-input"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="assistant-history-list">
              {loadingConversations ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                  Loading history...
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="assistant-history-empty">
                  <span className="assistant-history-empty-icon">💭</span>
                  <span style={{ fontWeight: 600, fontSize: '0.86rem', color: '#475569' }}>
                    {searchQuery ? 'No matching chats' : 'No conversations yet'}
                  </span>
                  <small style={{ fontSize: '0.76rem' }}>
                    {searchQuery ? 'Try another keyword' : 'Start with one of the quick suggestions!'}
                  </small>
                </div>
              ) : (
                filteredConversations.map((c) => {
                  const isActive = selected === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`assistant-history-item ${isActive ? 'is-active' : ''}`}
                      onClick={() => void open(c.id)}
                    >
                      <span className="assistant-history-icon">💬</span>
                      <div className="assistant-history-info">
                        <div className="assistant-history-label">{c.title}</div>
                        <div className="assistant-history-meta">
                          <span>{formatDate(c.updatedAt)}</span>
                          {c._count?.messages ? <span>· {c._count.messages} msgs</span> : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="assistant-history-delete-btn"
                        title="Delete conversation"
                        onClick={(e) => void handleDeleteConversation(c.id, e)}
                      >
                        ✕
                      </button>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* Center Panel: Interactive Chat Canvas */}
          <main className="assistant-chat-panel">
            <header className="assistant-chat-header">
              <div className="assistant-chat-header-title">
                <span className="assistant-chat-title-text">
                  {selectedConversation?.title ?? 'New SEO Conversation'}
                </span>
                <span className="assistant-chat-grounded-pill">
                  ✓ Verified Tenant Telemetry
                </span>
              </div>

              {selected && (
                <div className="assistant-chat-header-actions">
                  <button
                    type="button"
                    className="assistant-btn-secondary"
                    style={{ padding: '4px 10px', fontSize: '0.78rem', color: '#ef4444' }}
                    onClick={() => void handleDeleteConversation(selected)}
                  >
                    🗑️ Delete Chat
                  </button>
                </div>
              )}
            </header>

            {error && (
              <div style={{ margin: '12px 20px 0', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: '0.84rem' }}>
                ⚠️ {error}
              </div>
            )}

            {/* Scrollable Message Thread */}
            <div className="assistant-messages-thread" ref={scrollRef}>
              {messages.length === 0 ? (
                /* Hero Starter State */
                <div className="assistant-empty-hero">
                  <div className="assistant-empty-icon-wrap">
                    <span>✨</span>
                  </div>
                  <h2 className="assistant-empty-title">
                    How can I help you grow {business?.name ? `"${business.name}"` : 'your business'}?
                  </h2>
                  <p className="assistant-empty-subtitle">
                    Select a high-value prompt below or type your own question to inspect crawl audits, keyword opportunities, and Google metrics.
                  </p>

                  <div className="assistant-starter-grid">
                    {STARTER_OPTIONS.map((item) => (
                      <button
                        key={item.category}
                        type="button"
                        className="assistant-starter-card"
                        onClick={() => void send(undefined, item.prompt)}
                      >
                        <div className="assistant-starter-card-top">
                          <span className="assistant-starter-card-icon">{item.icon}</span>
                          <span className="assistant-starter-card-category">{item.category}</span>
                        </div>
                        <div className="assistant-starter-card-prompt">{item.prompt}</div>
                        <div className="assistant-starter-card-desc">{item.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Message Bubbles */
                messages.map((m) => <ConversationMessage key={m.id} message={m} />)
              )}

              {/* Animated Thinking State */}
              {sending && (
                <div className="assistant-message-row is-ai">
                  <div className="assistant-avatar ai">✨</div>
                  <div className="assistant-thinking-card">
                    <span>Analyzing real SEO data & rankings</span>
                    <div className="assistant-thinking-dots" aria-label="Thinking">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Docked Composer Input Bar */}
            <footer className="assistant-composer-container">
              {/* Quick Prompt Pill Tags */}
              <div className="assistant-quick-prompts">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className="assistant-quick-prompt-btn"
                    onClick={() => void send(undefined, p.prompt)}
                    disabled={sending}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Input Box with Auto-Expand & Send */}
              <div className="assistant-composer-box">
                <textarea
                  ref={textareaRef}
                  id="assistant-input-textarea"
                  className="assistant-composer-textarea"
                  value={input}
                  maxLength={2000}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Ask a question about ${business?.name ?? 'this business'}'s SEO, audit, keywords, or competitors...`}
                  disabled={sending}
                />
                <div className="assistant-composer-bottom">
                  <div className="assistant-composer-hints">
                    <span>↵ Enter to send · Shift+↵ for new line</span>
                    {input.length > 0 && <span>{input.length} / 2,000</span>}
                  </div>
                  <button
                    type="button"
                    className="assistant-send-btn"
                    onClick={() => void send()}
                    disabled={sending || !input.trim()}
                  >
                    <span>{sending ? 'Analyzing...' : 'Send'}</span>
                    <span>➤</span>
                  </button>
                </div>
              </div>
            </footer>
          </main>

          {/* Right Panel: Live Business Context Inspector */}
          {showContextPanel && (
            <aside className="assistant-context-panel" aria-label="Live Grounded Telemetry">
              <div className="assistant-context-header">
                <div className="assistant-context-title">
                  <span>📊 Live SEO Signals</span>
                </div>
                <span className="assistant-badge-pill" style={{ fontSize: '0.68rem' }}>
                  Tenant DB
                </span>
              </div>

              <div className="assistant-context-body">
                {/* Active Business Summary Card */}
                <div className="assistant-context-biz-card">
                  <div className="assistant-context-biz-name">{business?.name ?? 'Loading business...'}</div>
                  <div className="assistant-context-biz-sub">
                    {business?.industry ?? 'General Business'} · {business?.locations?.[0]?.city ?? 'Local Business'}
                  </div>
                  {business?.website && (
                    <div style={{ marginTop: 4, fontSize: '0.74rem', color: '#2563eb', wordBreak: 'break-all' }}>
                      {business.website.replace(/^https?:\/\//, '')}
                    </div>
                  )}
                </div>

                {/* Real Context Telemetry Links */}
                <div className="assistant-telemetry-group">
                  {/* Website Audit Signal */}
                  <Link to={`/businesses/${id}/seo`} className="assistant-telemetry-item">
                    <div className="assistant-telemetry-label-wrap">
                      <span className="assistant-telemetry-icon">🔍</span>
                      <span className="assistant-telemetry-name">Website Audit</span>
                    </div>
                    {latestAudit ? (
                      <span className={`assistant-telemetry-badge ${latestAudit.overallScore && latestAudit.overallScore >= 70 ? 'status-ok' : 'status-warn'}`}>
                        Score {latestAudit.overallScore ?? 'N/A'}
                      </span>
                    ) : (
                      <span className="assistant-telemetry-badge status-off">Pending</span>
                    )}
                  </Link>

                  {/* Google Search Console Signal */}
                  <Link to={`/businesses/${id}/integrations`} className="assistant-telemetry-item">
                    <div className="assistant-telemetry-label-wrap">
                      <span className="assistant-telemetry-icon">📊</span>
                      <span className="assistant-telemetry-name">Search Console</span>
                    </div>
                    <span className={`assistant-telemetry-badge ${googleStatus?.connected ? 'status-ok' : 'status-off'}`}>
                      {googleStatus?.connected ? 'Synced' : 'Not linked'}
                    </span>
                  </Link>

                  {/* Keywords Signal */}
                  <Link to={`/businesses/${id}/keywords`} className="assistant-telemetry-item">
                    <div className="assistant-telemetry-label-wrap">
                      <span className="assistant-telemetry-icon">🔑</span>
                      <span className="assistant-telemetry-name">Keywords</span>
                    </div>
                    <span className="assistant-telemetry-badge status-ok">
                      Active
                    </span>
                  </Link>

                  {/* SEO Opportunities Signal */}
                  <Link to={`/businesses/${id}/intelligence`} className="assistant-telemetry-item">
                    <div className="assistant-telemetry-label-wrap">
                      <span className="assistant-telemetry-icon">🎯</span>
                      <span className="assistant-telemetry-name">Opportunities</span>
                    </div>
                    <span className="assistant-telemetry-badge status-ok">
                      {opportunityCount > 0 ? `${opportunityCount} found` : 'Engine Active'}
                    </span>
                  </Link>

                  {/* Competitor Signal */}
                  <Link to={`/businesses/${id}/competitors`} className="assistant-telemetry-item">
                    <div className="assistant-telemetry-label-wrap">
                      <span className="assistant-telemetry-icon">⚔️</span>
                      <span className="assistant-telemetry-name">Competitors</span>
                    </div>
                    <span className="assistant-telemetry-badge status-ok">
                      {competitorCount > 0 ? `${competitorCount} tracked` : 'Tracked'}
                    </span>
                  </Link>
                </div>

                {/* Explanatory callout */}
                <div className="assistant-context-explainer">
                  <strong>💡 Hallucination-Safe Reasoning:</strong> Responses are grounded exclusively in your organization's stored crawl data, queries, and ranking signals.
                </div>

                {/* Direct Link to SEO Intelligence */}
                <Link
                  to={`/businesses/${id}/intelligence`}
                  className="assistant-btn-secondary"
                  style={{ textAlign: 'center', justifyContent: 'center', marginTop: 'auto' }}
                >
                  Open SEO Intelligence →
                </Link>
              </div>
            </aside>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
